'use client';

import { Fragment, memo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AvailabilityDayCard } from '@/components/tournament/details-page/participants/AvailabilityDayCard';
import type { Availability } from '@/types/tournament.model';

interface TeamsAvailabilityComparisonProps {
  team1Availability?: Availability[];
  team2Availability?: Availability[];
  /** The tournament's own slots: when given, each day only shows the buckets it offers. */
  schedule?: Availability[];
}

// Literal class names so Tailwind generates them.
const COLUMN_START = ['lg:col-start-1', 'lg:col-start-2'];

/** Every range declared for `date`, however many entries carry it, like `fitsAvailability`. */
const rangesOn = (entries: Availability[], date: string) =>
  entries.filter((e) => e.date === date).flatMap((e) => e.timeRange ?? []);

/**
 * Both teams' availability side by side, one grid row per date. A day only one team declared
 * still gets a card on the other side (every bucket red), so a row never pairs two different
 * days and both cells of a row stretch to the same height. A side with nothing to show gets a
 * neutral note instead of a column of red days: the list also comes back empty when there is no
 * pair yet (an undecided bracket or grid slot) or when a dependent side's candidates share no day,
 * and none of those means "can't play".
 *
 * Every cell is pinned to its team's column, so grid auto-placement keeps each team in its own
 * column even when the other one has no cards. Below `lg` the grid collapses into one column,
 * interleaving the teams date by date, so each card carries its team label there instead of
 * relying on the column titles.
 *
 * Memoized because the modal around it re-renders on every keystroke of its date/time inputs.
 */
export const TeamsAvailabilityComparison = memo(
  function TeamsAvailabilityComparison({
    team1Availability,
    team2Availability,
    schedule,
  }: TeamsAvailabilityComparisonProps) {
    const [isCompact, setIsCompact] = useState(true);

    const teams = [
      { label: 'Equipo 1', availability: team1Availability ?? [] },
      { label: 'Equipo 2', availability: team2Availability ?? [] },
    ];
    // One entry per date across both teams; `dayOfWeek` follows from the date, so either will do.
    const days = [
      ...new Map(
        teams.flatMap((t) => t.availability).map((d) => [d.date, d]),
      ).values(),
    ].sort((a, b) => a.date.localeCompare(b.date));

    if (days.length === 0) return null;

    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-transparent"
            onClick={() => setIsCompact((prev) => !prev)}
          >
            {isCompact
              ? 'Ver disponibilidades (detalle)'
              : 'Colapsar disponibilidades'}
          </Button>
        </div>

        <div className="grid gap-3 lg:grid-cols-2 lg:gap-x-4">
          {teams.map(({ label, availability }, i) => (
            <Fragment key={label}>
              {/* Below `lg` only an empty team keeps its title: the others' cards are labeled. */}
              <h4
                className={cn(
                  'font-semibold text-gray-900',
                  availability.length > 0 && 'hidden lg:block',
                )}
              >
                Disponibilidad {label}
              </h4>
              {/* Row 2 rather than under the title, where it'd make the title row taller than
                the other team's and push that team's first card down. */}
              {availability.length === 0 && (
                <p
                  className={cn(
                    'text-sm text-gray-500 lg:row-start-2',
                    COLUMN_START[i],
                  )}
                >
                  Sin disponibilidad para mostrar.
                </p>
              )}
            </Fragment>
          ))}

          {days.map(({ date, dayOfWeek }) => {
            const scheduleSlots = schedule && rangesOn(schedule, date);
            return (
              <Fragment key={date}>
                {teams.map(
                  ({ label, availability }, i) =>
                    availability.length > 0 && (
                      <div
                        key={label}
                        className={cn(
                          'flex min-w-0 flex-col gap-1',
                          COLUMN_START[i],
                        )}
                      >
                        <p className="text-xs font-medium text-gray-500 lg:hidden">
                          {label}
                        </p>
                        <AvailabilityDayCard
                          className="flex-1"
                          date={date}
                          dayOfWeek={dayOfWeek}
                          slots={rangesOn(availability, date)}
                          // No schedule entry for the date: show every bucket, as the
                          // participants card does.
                          scheduleSlots={
                            scheduleSlots?.length ? scheduleSlots : undefined
                          }
                          isCompact={isCompact}
                          stackBucketHeaders
                        />
                      </div>
                    ),
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  },
);
